<template>
  <div class="min-h-screen p-6">
    <div class="max-w-7xl mx-auto mb-8">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-[16px] sm:text-3xl font-bold text-slate-800 mb-2">
            Class Management
          </h1>
          <p class="text-[9px] sm:text-sm text-slate-600">
            Manage your classes efficiently
          </p>
        </div>
        <button
          @click="switchView(viewMode === 'list' ? 'create' : 'list')"
          class="bg-blue-600 hover:bg-blue-700 text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl font-medium transition-all duration-200 flex items-center gap-2"
        >
          <template v-if="viewMode === 'list'">
            <Plus class="w-5 h-5" />
            <span class="text-[9px] sm:text-base">New Class</span>
          </template>
          <template v-else>
            <List class="w-5 h-5" />
            <span class="text-[9px] sm:text-base">View Classes</span>
          </template>
        </button>
      </div>
    </div>

    <!-- List -->
    <div
      v-if="viewMode === 'list'"
      class="max-w-7xl mx-auto"
    >
      <div
        v-if="isLoadingClasses"
        class="fixed inset-0 z-[2000] flex items-center justify-center bg-black/20 backdrop-blur-sm"
      >
        <div class="flex flex-col items-center space-y-6">
          <div class="relative">
            <div class="w-12 h-12 border-4 border-blue-200 rounded-full"></div>
            <div
              class="absolute top-0 left-0 w-12 h-12 border-4 border-blue-600 rounded-full border-t-transparent animate-spin"
            ></div>
          </div>
          <div class="text-center">
            <p class="text-slate-700 font-semibold text-balance">
              Loading classes
            </p>
            <p class="text-slate-500 text-sm text-balance">
              Please wait a moment...
            </p>
          </div>
        </div>
      </div>

      <div
        v-if="
          classes.length === 1 &&
          ['passout', 'Passout'].includes(classes[0].name)
        "
        class="text-center py-20"
      >
        <div
          class="bg-white rounded-xl p-10 shadow-sm border border-slate-200 max-w-md mx-auto"
        >
          <School class="w-14 h-14 text-slate-300 mx-auto mb-5" />
          <h3 class="text-lg sm:text-xl font-semibold text-slate-800 mb-2">
            No classes yet
          </h3>
          <p class="text-slate-600 mb-5">
            Create your first class to get started
          </p>
          <div class="flex gap-3 justify-center">
            <button
              @click="switchView('create')"
              class="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl font-medium transition-all duration-200 flex items-center gap-2"
            >
              <Plus class="w-5 h-5" />
              Create Class
            </button>
            <button
              @click="showEmptyStateCreateDialog = true"
              class="bg-slate-100 hover:bg-slate-200 text-slate-700 px-5 py-2.5 rounded-xl font-medium transition-all duration-200 flex items-center gap-2"
            >
              <Users class="w-5 h-5" />
              Bulk Create
            </button>
          </div>
        </div>
      </div>

      <div
        v-else
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        <div
          v-for="cls in classes"
          :key="cls.id || cls._id"
          :class="[
            'rounded-2xl p-6 border transition-all duration-300 relative',
            cls.name && cls.name.toLowerCase() === 'passout'
              ? 'bg-slate-50 border-slate-300 shadow-none hover:shadow-none hover:translate-y-0'
              : 'bg-white shadow-sm border-slate-200 hover:shadow-xl hover:-translate-y-1',
          ]"
        >
          <!-- 3-dot menu -->
          <div v-if="cls.name.toLowerCase() !== 'passout'">
            <div class="absolute top-4 right-4">
              <div
                class="relative"
                @click.stop="toggleMenu(cls._id)"
              >
                <MoreVertical
                  class="w-5 h-5 text-black cursor-pointer hover:text-slate-700"
                />

                <div
                  v-if="openMenuId === cls._id"
                  class="absolute right-0 mt-2 w-36 bg-white rounded-lg shadow-lg border border-slate-200 z-50"
                >
                  <button
                    @click="startEdit(cls)"
                    class="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-600 transition"
                  >
                    <Edit class="w-4 h-4" /> Edit
                  </button>
                  <button
                    @click="
                      confirmDelete(cls);
                      closeMenu();
                    "
                    class="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-red-50 hover:text-red-600 transition"
                  >
                    <Trash2 class="w-4 h-4" /> Delete
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Header -->
          <div class="flex items-start gap-4 mb-5">
            <div
              :class="[
                'w-12 h-12 rounded-xl flex items-center justify-center shadow-inner',
                cls.name && cls.name.toLowerCase() === 'passout'
                  ? 'bg-slate-100'
                  : 'bg-gradient-to-r from-blue-100 to-blue-200',
              ]"
            >
              <BookOpen
                :class="[
                  'w-6 h-6',
                  cls.name && cls.name.toLowerCase() === 'passout'
                    ? 'text-slate-500'
                    : 'text-blue-600',
                ]"
              />
            </div>
            <div class="flex-1 min-w-0">
              <h3 class="font-semibold text-slate-800 text-lg truncate">
                {{ cls.name }}
              </h3>
              <div v-if="cls.name.toLowerCase() !== 'passout'">
                <p
                  class="text-xs inline-block mt-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium"
                >
                  {{ cls.section || "No section" }}
                </p>
              </div>
            </div>
          </div>

          <!-- Stats -->
          <div class="grid grid-cols-2 gap-4 mb-6">
            <div class="bg-slate-50 p-3 rounded-lg text-center">
              <p class="text-xl font-bold text-slate-800">
                {{ cls.totalStudents }}
              </p>
              <p class="text-xs text-slate-500">Total Students</p>
            </div>
            <div v-if="cls.name.toLowerCase() !== 'passout'">
              <div class="bg-green-50 p-3 rounded-lg text-center">
                <p class="text-xl font-bold text-green-700">
                  {{ cls.totalPresentToday }}
                </p>
                <p class="text-xs text-slate-500">Present Today</p>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="flex justify-end">
            <router-link
              :to="{ path: '/students', query: { classId: cls._id || cls.id } }"
              class="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500 text-white text-sm font-medium shadow hover:bg-blue-600 transition-all"
            >
              <Users class="w-4 h-4" />
              View Students
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <!-- Create -->
    <div
      v-else-if="viewMode === 'create'"
      class="max-w-md mx-auto"
    >
      <div
        class="bg-white rounded-xl p-6 w-full max-w-xl shadow-sm border border-slate-200"
      >
        <div class="text-center mb-6">
          <div
            class="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-3"
          >
            <Plus class="w-7 h-7 text-blue-600" />
          </div>
          <h2 class="text-xl font-bold text-slate-800 mb-1">
            Create New Class
          </h2>
          <p class="text-slate-600 text-sm">
            Add a new class to your management system
          </p>
        </div>

        <form
          @submit.prevent="createClass"
          class="space-y-5"
        >
          <div>
            <label class="block text-sm font-medium text-slate-700 mb-2">
              Class Name <span class="text-red-500">*</span>
            </label>
            <input
              type="text"
              v-model="newClassName"
              required
              class="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Mathematics Grade 10"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-slate-700 mb-2"
              >Section (Optional)</label
            >
            <input
              type="text"
              v-model="newClassSection"
              class="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Section A"
            />
          </div>

          <button
            type="submit"
            :disabled="isSubmitting"
            class="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white py-2.5 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2"
          >
            <Loader2
              v-if="isSubmitting"
              class="w-5 h-5 animate-spin"
            />
            <span v-else>Create Class</span>
          </button>
        </form>
      </div>
    </div>

    <!-- Edit -->
    <div
      v-else-if="viewMode === 'edit'"
      class="max-w-md mx-auto"
    >
      <div
        class="bg-white rounded-xl p-6 w-full max-w-xl shadow-sm border border-slate-200"
      >
        <div class="text-center mb-6">
          <div
            class="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-3"
          >
            <Edit class="w-7 h-7 text-green-600" />
          </div>
          <h2 class="text-xl font-bold text-slate-800 mb-1">Edit Class</h2>
          <p class="text-slate-600 text-sm">Update class information</p>
        </div>

        <form
          @submit.prevent="editClass"
          class="space-y-5"
        >
          <div>
            <label class="block text-sm font-medium text-slate-700 mb-2">
              Class Name <span class="text-red-500">*</span>
            </label>
            <input
              type="text"
              v-model="editClassName"
              required
              class="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="e.g., Mathematics Grade 10"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-slate-700 mb-2"
              >Section (Optional)</label
            >
            <input
              type="text"
              v-model="editClassSection"
              class="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="e.g., Section A"
            />
          </div>

          <div class="flex gap-3">
            <button
              type="button"
              @click="cancelEdit"
              class="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 py-2.5 rounded-lg font-medium transition-all duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              :disabled="isSubmitting"
              class="flex-1 py-2.5 rounded-lg font-medium text-white transition-all duration-200"
              :class="[isSubmitting ? '' : 'bg-green-600 hover:bg-green-700']"
            >
              <Loader2
                v-if="isSubmitting"
                class="w-5 h-5 animate-spin"
              />
              <span v-else>Update</span>
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Delete Confirmation Dialog (KEPT) -->
    <Transition name="modal">
      <div
        v-if="showConfirmationDialog"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        <div
          class="absolute inset-0 bg-black/20 backdrop-blur-sm"
          @click="handleConfirmation(false)"
        ></div>
        <div
          class="relative bg-white rounded-xl p-6 w-full max-w-sm shadow-md border border-slate-200"
        >
          <div class="text-center">
            <div
              class="w-14 h-14 mx-auto mb-4 rounded-xl flex items-center justify-center bg-red-100"
            >
              <Trash2 class="w-7 h-7 text-red-600" />
            </div>
            <h3 class="text-lg font-semibold text-slate-800 mb-2">
              {{ confirmationTitle }}
            </h3>
            <p class="text-slate-600 mb-5 text-sm">{{ confirmationMessage }}</p>
            <div class="flex gap-3">
              <button
                @click="handleConfirmation(false)"
                class="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 py-2.5 rounded-lg font-medium transition-all duration-200"
              >
                Cancel
              </button>
              <button
                @click="handleConfirmation(true)"
                class="flex-1 py-2.5 rounded-lg font-medium text-white transition-all duration-200 bg-red-600 hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Empty state bulk create dialog -->
    <Transition name="modal">
      <div
        v-if="showEmptyStateCreateDialog"
        class="fixed inset-0 z-[1005] flex items-center justify-center p-4"
      >
        <div class="absolute inset-0 bg-black/20 backdrop-blur-sm"></div>
        <div
          class="relative bg-white rounded-xl p-6 w-full max-w-5xl shadow-md border border-slate-200 max-h-[90vh] overflow-y-auto"
        >
          <div class="mb-6">
            <div class="flex items-center gap-3 mb-2">
              <Users class="w-6 h-6 text-blue-600" />
              <h3 class="text-xl font-bold text-slate-800">Create Classes</h3>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <!-- Left: defaults with per-class sections -->
            <div class="md:col-span-2">
              <div class="flex items-center justify-between mb-3">
                <span class="text-sm font-medium text-slate-700"
                  >Default Classes (1–10)</span
                >
                <label
                  class="flex items-center gap-2 text-sm text-slate-600 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    v-model="bulkSelectAll"
                    @change="handleToggleAll"
                  />
                  Select all
                </label>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div
                  v-for="tpl in bulkTemplates"
                  :key="tpl.id"
                  class="rounded-lg border border-slate-200 p-3"
                >
                  <div class="flex items-center justify-between gap-2">
                    <label class="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        v-model="tpl.selected"
                        @change="handleToggleOne"
                      />
                      <span class="text-sm text-slate-700">{{ tpl.name }}</span>
                    </label>
                    <button
                      type="button"
                      @click.stop="tpl.adding = !tpl.adding"
                      class="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 border border-blue-200 px-2 py-1 rounded-md text-xs"
                      :aria-expanded="tpl.adding ? 'true' : 'false'"
                      :aria-label="`Add section to ${tpl.name}`"
                    >
                      <Plus class="w-4 h-4" />
                      Add section
                    </button>
                  </div>

                  <div
                    v-if="tpl.adding"
                    class="flex items-center gap-2 mt-2"
                  >
                    <input
                      v-model="tpl.newSection"
                      @keyup.enter="addSection(tpl)"
                      type="text"
                      class="w-full px-2 py-1 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., A"
                    />
                    <button
                      type="button"
                      @click.stop="addSection(tpl)"
                      class="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-xs"
                    >
                      Add
                    </button>
                  </div>

                  <div
                    v-if="tpl.sections.length"
                    class="flex flex-wrap gap-2 mt-2"
                  >
                    <span
                      v-for="(sec, idx) in tpl.sections"
                      :key="sec + idx"
                      class="inline-flex items-center gap-1 bg-slate-100 text-slate-700 px-2 py-1 rounded text-xs"
                    >
                      <span>Section {{ sec }}</span>
                      <button
                        type="button"
                        @click.stop="removeSection(tpl, idx)"
                        class="text-slate-500 hover:text-red-600"
                        :aria-label="`Remove section ${sec} from ${tpl.name}`"
                      >
                        <X class="w-3 h-3" />
                      </button>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right: global section + custom class rows -->
            <div class="space-y-6">
              <!-- Global section -->
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-2"
                  >Section (optional)</label
                >
                <input
                  v-model="bulkSection"
                  type="text"
                  class="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Section A"
                />
                <p class="text-[11px] text-slate-500 mt-1">
                  Per-class sections override this global section.
                </p>
              </div>

              <!-- Custom Classes -->
              <div>
                <div class="flex items-center justify-between mb-2">
                  <h4 class="text-sm font-medium text-slate-700">
                    Custom Classes
                  </h4>
                  <button
                    type="button"
                    @click="addCustomClassRow"
                    class="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 border border-blue-200 px-2 py-1 rounded-md text-xs"
                    aria-label="Add more class"
                  >
                    <Plus class="w-4 h-4" />
                    Add more class
                  </button>
                </div>

                <div
                  v-if="!customClasses.length"
                  class="text-xs text-slate-500"
                >
                  Click "Add more class" to add your own class names.
                </div>

                <div class="space-y-3">
                  <div
                    v-for="(row, idx) in customClasses"
                    :key="idx"
                    class="rounded-lg border border-slate-200 p-3"
                  >
                    <div class="flex items-start gap-2">
                      <div class="flex-1">
                        <label class="block text-[11px] text-slate-600 mb-1"
                          >Class name</label
                        >
                        <input
                          v-model="row.name"
                          type="text"
                          class="w-full px-2 py-1.5 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., Physics 12"
                        />
                      </div>
                      <div class="w-36">
                        <label class="block text-[11px] text-slate-600 mb-1"
                          >Section</label
                        >
                        <input
                          v-model="row.section"
                          type="text"
                          class="w-full px-2 py-1.5 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., B"
                        />
                      </div>
                      <button
                        type="button"
                        @click="removeCustomClassRow(idx)"
                        class="mt-6 text-slate-500 hover:text-red-600 p-2 rounded-md"
                        aria-label="Remove custom class"
                      >
                        <X class="w-4 h-4" />
                      </button>
                    </div>
                    <p
                      v-if="rowError(idx)"
                      class="mt-2 text-[11px] text-red-600"
                    >
                      Class name is required.
                    </p>
                  </div>
                </div>
                <p class="text-[11px] text-slate-500 mt-2">
                  Leave Section empty to use the global section above.
                </p>
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div class="flex flex-col sm:flex-row gap-3 mt-6">
            <!-- <button
              class="bg-slate-100 hover:bg-slate-200 text-slate-700 py-2.5 px-4 rounded-lg font-medium transition-all duration-200"
              @click="
                switchView('create');
                showEmptyStateCreateDialog = false;
              "
            >
              Create a single class
            </button> -->
            <button
              class="sm:ml-auto bg-blue-600 hover:bg-blue-700 text-white py-2.5 px-4 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 disabled:bg-slate-300"
              :disabled="isSubmitting || selectedCount === 0"
              @click="handleBulkCreate"
            >
              <Loader2
                v-if="isSubmitting"
                class="w-5 h-5 animate-spin"
              />
              <template v-else>
                <Plus class="w-5 h-5" />
                Create {{ selectedCount }} selected
              </template>
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted, watch } from "vue";
  import { useStore } from "vuex";
  import {
    Plus,
    List,
    School,
    BookOpen,
    MoreVertical,
    Loader2,
    Edit,
    Trash2,
    Users,
    X,
  } from "lucide-vue-next";
  import { useGlobalToast } from "@/utils/useGlobalToast";
  const { showToast } = useGlobalToast();
  import { useRouter } from "vue-router";

  const store = useStore();
  const router = useRouter();

  const TotalTeachers = computed(
    () => store.getters.getOnboardingTotalTeachers,
  );
  const TotalStudents = computed(
    () => store.getters.getOnboardingTotalStudents,
  );

  const refreshTeachersAndStudents = async () => {
    try {
      // Fetch the total teacher count for onboarding
      await store.dispatch("fetchTotalTeacherCountOnboarding");

      // Fetch the total student count for onboarding
      await store.dispatch("fetchTotalStudentCountOnboarding");
    } catch (error) {
      console.error("❌ Error refreshing teachers:", error);
    }
  };

  const viewMode = ref("list");
  const newClassName = ref("");
  const newClassSection = ref("");
  const editClassName = ref("");
  const editClassSection = ref("");
  const editingClass = ref(null);
  const isSubmitting = ref(false);
  const showConfirmationDialog = ref(false);
  const isLoadingClasses = ref(false);
  const confirmationType = ref("");
  const confirmationTitle = ref("");
  const confirmationMessage = ref("");
  const classToDelete = ref(null);

  const showEmptyStateCreateDialog = ref(false);
  const hasShownEmptyDialog = ref(false);
  const openMenuId = ref(null);

  function toggleMenu(id) {
    openMenuId.value = openMenuId.value === id ? null : id;
  }

  function closeMenu() {
    openMenuId.value = null;
  }

  // Close menu when clicking outside
  document.addEventListener("click", () => {
    openMenuId.value = null;
  });

  // Default templates with optional per-class sections
  const bulkTemplates = ref(
    Array.from({ length: 10 }, (_, i) => ({
      id: i + 1,
      name: `Class ${i + 1}`,
      selected: true,
      sections: [],
      adding: false,
      newSection: "",
    })),
  );
  const bulkSelectAll = ref(true);
  const bulkSection = ref("");

  // Custom class rows for "Add more class"
  const customClasses = ref([]); // [{ name: '', section: '' }]

  const classes = computed(() => {
    const list = store.getters.allClasses || [];

    // Separate "passout" classes
    const passoutClasses = list.filter(
      (cls) => cls.name?.toLowerCase() === "passout",
    );
    const normalClasses = list.filter(
      (cls) => cls.name?.toLowerCase() !== "passout",
    );

    // Sort normal classes numerically by the number in the name
    normalClasses.sort((a, b) => {
      const numA = parseInt(a.name.replace(/\D/g, "")) || 0;
      const numB = parseInt(b.name.replace(/\D/g, "")) || 0;
      return numA - numB;
    });

    // Put passout classes at the end
    return [...normalClasses, ...passoutClasses];
  });

  // Count considering per-class sections and custom rows
  const defaultsSelectedCount = computed(() =>
    bulkTemplates.value
      .filter((t) => t.selected)
      .reduce(
        (sum, t) => sum + (t.sections.length > 0 ? t.sections.length : 1),
        0,
      ),
  );

  // Only count custom rows that have a class name
  const customValidCount = computed(
    () => customClasses.value.filter((r) => (r.name || "").trim()).length,
  );

  const selectedCount = computed(
    () => defaultsSelectedCount.value + customValidCount.value,
  );

  onMounted(async () => {
    try {
      isLoadingClasses.value = true;
      await store.dispatch("fetchAllClasses");
    } catch (error) {
      console.error("Failed to load classes:", error);
    } finally {
      isLoadingClasses.value = false;
    }
  });

  watch(
    () => ({ loading: isLoadingClasses.value, list: classes.value }),
    ({ loading, list }) => {
      if (loading || !Array.isArray(list)) return;

      // Filter out "passout" class if it's the only one
      const validClasses = list.filter(
        (cls) => cls?.name?.toLowerCase() !== "passout",
      );

      if (validClasses.length === 0 && !hasShownEmptyDialog.value) {
        showEmptyStateCreateDialog.value = true;
        hasShownEmptyDialog.value = true;
      }
    },
  );

  const switchView = (mode) => {
    viewMode.value = mode;
    if (mode === "create") {
      newClassName.value = "";
      newClassSection.value = "";
    } else if (mode === "list") {
      editingClass.value = null;
    }
  };

  const startEdit = (cls) => {
    editingClass.value = cls;
    editClassName.value = cls.name;
    editClassSection.value = cls.section || "";
    closeMenu(); // Close menu immediately
    // Use nextTick to ensure menu closes before switching view
    setTimeout(() => {
      viewMode.value = "edit";
    }, 50);
  };

  const cancelEdit = () => {
    editingClass.value = null;
    editClassName.value = "";
    editClassSection.value = "";
    viewMode.value = "list";
  };

  const confirmDelete = (cls) => {
    classToDelete.value = cls;
    confirmationType.value = "delete";
    confirmationTitle.value = "Delete Class";
    confirmationMessage.value = `This will permanently delete "${cls.name}". This action cannot be undone.`;
    showConfirmationDialog.value = true;
  };

  const handleConfirmation = async (confirmed) => {
    showConfirmationDialog.value = false;
    if (confirmed && confirmationType.value === "delete") {
      await deleteClass();
    }
    classToDelete.value = null;
  };

  const createClass = async () => {
    if (!newClassName.value.trim()) {
      showToast("error", "Please enter a class name.");

      return;
    }

    isSubmitting.value = true;
    try {
      const payload = {
        name: newClassName.value.trim(),
        section: newClassSection.value.trim() || null,
      };
      await store.dispatch("createClass", payload);
      newClassName.value = "";
      newClassSection.value = "";
      showToast("success", `"${payload.name}" has been created successfully!`);
      await store.dispatch("fetchAllClasses");

      // Refresh teacher and student counts
      await refreshTeachersAndStudents();

      // Check totals after creation
      // if (TotalTeachers.value === 0 && TotalStudents.value === 0) {
      //   router.replace("/onboarding");
      //   return;
      // }
      switchView("list");
    } catch (error) {
      showToast(
        "error",
        error?.response?.data?.message ||
          "Failed to create class. Please try again.",
      );
    } finally {
      isSubmitting.value = false;
    }
  };

  const editClass = async () => {
    if (!editClassName.value.trim()) {
      showToast("error", "Please enter a class name.");

      return;
    }

    isSubmitting.value = true;
    try {
      const updates = {
        name: editClassName.value.trim(),
        section: editClassSection.value.trim() || null,
      };
      await store.dispatch("editClassById", {
        classId: editingClass.value._id || editingClass.value.id,
        updates,
      });
      showToast("success", `"${updates.name}" has been updated successfully!`);
      await store.dispatch("fetchAllClasses");
      switchView("list");
    } catch (error) {
      showToast(
        "error",
        error?.response?.data?.message ||
          "Failed to update class. Please try again.",
      );
    } finally {
      isSubmitting.value = false;
    }
  };

  const deleteClass = async () => {
    isSubmitting.value = true;
    try {
      await store.dispatch(
        "deleteClassById",
        classToDelete.value._id || classToDelete.value.id,
      );
      showToast(
        "success",
        `"${classToDelete.value.name}" has been deleted successfully.`,
      );
      await store.dispatch("fetchAllClasses");
    } catch (error) {
      showToast(
        "error",
        error?.response?.data?.message ||
          "Failed to delete class. Please try again.",
      );
    } finally {
      isSubmitting.value = false;
    }
  };

  const handleToggleAll = () => {
    bulkTemplates.value.forEach((t) => (t.selected = bulkSelectAll.value));
  };

  const handleToggleOne = () => {
    const all = bulkTemplates.value.every((t) => t.selected);
    const none = bulkTemplates.value.every((t) => !t.selected);
    bulkSelectAll.value = all && !none;
  };

  // Per-class section helpers
  const addSection = (tpl) => {
    const val = (tpl.newSection || "").trim();
    if (!val) return;
    const exists = tpl.sections.some(
      (s) => s.toLowerCase() === val.toLowerCase(),
    );
    if (!exists) tpl.sections.push(val);
    tpl.newSection = "";
    tpl.adding = false;
  };

  const removeSection = (tpl, idx) => {
    tpl.sections.splice(idx, 1);
  };

  // Custom class row helpers
  const addCustomClassRow = () => {
    customClasses.value.push({ name: "", section: "" });
  };
  const removeCustomClassRow = (idx) => {
    customClasses.value.splice(idx, 1);
  };
  const rowError = (idx) => {
    const r = customClasses.value[idx];
    return r && !(r.name || "").trim();
  };

  const handleBulkCreate = async () => {
    // Build payloads from defaults
    const selected = bulkTemplates.value.filter((t) => t.selected);
    const globalSection = (bulkSection.value || "").trim() || null;

    const defaultPayloads = selected.flatMap((t) => {
      if (t.sections.length > 0) {
        return t.sections.map((sec) => ({ name: t.name, section: sec }));
      }
      return [{ name: t.name, section: globalSection }];
    });

    // Build payloads from custom rows (require name, section optional -> fallback to global)
    const customPayloads = customClasses.value
      .filter((r) => (r.name || "").trim())
      .map((r) => ({
        name: r.name.trim(),
        section: (r.section || "").trim() || globalSection,
      }));

    const payloads = [...defaultPayloads, ...customPayloads];

    if (payloads.length === 0) {
      showToast("error", "Please select or add at least one class to create.");
      return;
    }

    // Validate: prevent duplicate empty names etc. (basic)
    const invalidCustom = customClasses.value.some(
      (r) => (r.name || "").trim() === "",
    );
    if (invalidCustom) {
      showToast("error", "Custom classes must have a class name.");

      return;
    }

    isSubmitting.value = true;
    try {
      const results = await Promise.allSettled(
        payloads.map((p) => store.dispatch("createClass", p)),
      );
      const successCount = results.filter(
        (r) => r.status === "fulfilled",
      ).length;
      const failCount = results.length - successCount;

      await store.dispatch("fetchAllClasses");

      const message =
        successCount > 0
          ? `Created ${successCount} class${successCount > 1 ? "es" : ""}${
              failCount ? ` (${failCount} failed)` : ""
            }.`
          : "Failed to create classes. Please try again.";

      showToast(successCount > 0 ? "success" : "error", message);

      // Reset custom rows after success
      if (successCount > 0) {
        customClasses.value = [];
      }

      showEmptyStateCreateDialog.value = false;

      // Refresh teacher and student counts
      await refreshTeachersAndStudents();
      // Check totals after creation
      if (TotalTeachers.value == 0 && TotalStudents.value == 0) {
        router.replace("/onboarding");
        return;
      }
    } catch (error) {
      showToast("error", "Failed to create classes. Please try again.");
    } finally {
      isSubmitting.value = false;
    }
  };
</script>

<style scoped>
  .modal-enter-active {
    transition: all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  .modal-leave-active {
    transition: all 0.2s ease-in;
  }
  .modal-enter-from,
  .modal-leave-to {
    opacity: 0;
    transform: translateY(8px) scale(0.98);
  }
</style>
